(ns shared.core.components.input
  (:require [shared.core.reactive :as re]
            [shared.core.style :as style]
            [shared.core.components.label :as lbl]
            ["semantic-ui-react" :as semantic-ui]
            [shared.core.components.icon :as icon]
            [shared.core.util :as util]
            [clojure.string :as s]))

;; https://react.semantic-ui.com/elements/input/
;; https://react.semantic-ui.com/addons/text-area/

(def base-style {:border-width "1px"
                 :border-style "solid"
                 :border-radius "3px"
                 :color (style/color :gray :base)
                 :font-family style/font-family
                 :font-size "14px"
                 :outline "none"
                 :padding "7px 20px 7px 8px"
                 :max-width "100%"
                 :width "100%"})

(def primary-base-style (merge base-style
                               {:color (style/color :on-background :base)
                                :font-family style/dm-sans-font-family}))

(def round-floating-base-style {:background-color (style/color :white :base)
                                :border (str "1px solid " (style/color :gray :shade-18))
                                :border-radius "80px"
                                :box-shadow "4px 7px 6.2px 0px rgba(0, 0, 0, 0.10)"
                                :color (style/color :black :base)
                                :padding "8px 12px"
                                :font-size "13px"
                                :font-weight style/font-medium-weight
                                :line-height 1.2
                                :height "40px"})

(def theme-base-style {:dark {:color (str (style/dark-color :on-background :base) " !important")}})

(def input-style {:background-color (style/color :gray :shade-2)
                  :border-color (style/color :gray :shade-1)})
(def theme-input-style {:dark {:background-color (style/dark-color :background :lighter-1)
                               :border-color (style/dark-color :on-surface :base)}})

(def input-focus-style {:background-color (style/color :white :base)
                        :border-color (style/color :green :base)
                        :color (style/color :gray :base)})
(def primary-input-focus-style {:background-color (style/color :background :base)
                                :border-color (style/color :primary :base)
                                :color (style/color :on-background :base)})
(def round-floating-input-focus-style {:background-color (style/color :white :base)
                                       :border (str "2px solid " (style/color :gray :shade-18))
                                       :color (style/color :black :base)
                                       :margin-left "-1px"})
(def theme-input-focus-style {:dark {:background-color (style/dark-color :background :base)
                                     :border-color (style/dark-color :primary :base)
                                     :color (style/dark-color :on-background :base)}})

(def large-input-style {:font-size "16px"
                        :padding "9px 18px 10px 11px"})

(def disabled-style {:opacity 0.5})

(style/add-css [".ui.input input, .ui.textarea" (merge base-style input-style)]
               [".ui.input input[type='number']" {:padding "7px 8px"}]
               [".ui.input.primary-input input" (merge primary-base-style input-style)]
               [".ui.input.round-floating-input input" (merge input-style round-floating-base-style)]
               [".ui.input.dark input, .ui.textarea.dark" (merge (get theme-base-style :dark)
                                                                 (get theme-input-style :dark))]
               [".ui.input input.clearable" {:padding "7px 27px 7px 8px"}]
               [".ui.input input.post-contents" {:padding "7px 27px 7px 8px"}]
               [".ui.input.pre-icon input" {:padding "7px 8px 7px 27px"}]
               [".ui.input.round-floating-input.pre-icon input" {:padding "8px 12px 8px 42px"}]
               [".ui.input.round-floating-input.pre-icon input::placeholder, .ui.textarea.dark::placeholder"
                {:color (style/color :gray :shade-16)
                 :opacity 1}]
               [".ui.input.pre-contents input" {:padding "7px 8px 7px 27px"}]
               [".ui.input.round-floating-input.pre-contents input" {:padding "8px 12px 8px 42px"}]
               [".ui.input.round-floating-input.pre-contents input::placeholder, .ui.textarea.dark::placeholder"
                {:color (style/color :gray :shade-16)
                 :opacity 1}]
               [".ui.input input.no-border-radius input, .ui.input.large.no-border-radius input" {:border-radius "0"}]
               [".ui.input.large input, .ui.textarea.large" large-input-style]
               [".ui.input.large.clearable input" {:padding "9px 30px 10px 11px"}]
               [".ui.input.large.post-contents input" {:padding "9px 30px 10px 11px"}]
               [".ui.input input:focus, .ui.input>input:focus, .ui.textarea:focus" input-focus-style]
               [".ui.primary-input.input input:focus, .ui.input.primary-input>input:focus" primary-input-focus-style]
               [".ui.round-floating-input.input input:focus, .ui.input.round-floating-input>input:focus" round-floating-input-focus-style]
               [".ui.input.dark input:focus, .ui.input.dark>input:focus, .ui.textarea.dark:focus" (get theme-input-focus-style :dark)]
               [".ui.input.dark input::placeholder, .ui.textarea.dark::placeholder" {:color (style/dark-color :on-background :base)
                                                                                     :opacity 0.7}]
               [".ui.input.dark input:focus::placeholder" {:opacity 0.7}]
               [".ui.textarea::placeholder" {:opacity 0.35}]
               [".ui.textarea:focus::placeholder" {:opacity 0.83}]
               [".ui.input input[disabled], textarea[disabled].ui.textarea" disabled-style])

(defn input [{:keys [val-subscribe form-path form-name focused?* form-change-debounce-time]}]
  (let [val- (if val-subscribe
               (re/subscribe val-subscribe)
               (when (and form-path form-name)
                 (re/subscribe [:form/val- form-path form-name])))
        theme- (re/subscribe [:core/theme-])
        focused?* (or focused?* (re/ratom false))
        debounced-form-change (some->> form-change-debounce-time
                                       (util/debounce #(re/dispatch [:form/change form-path form-name %])))]
    (re/create-class
      {:component-did-update
       (fn [this]
         ;; We'll have to monitor this to make sure everything is still working appropriately
         ;; If we turn this into a controlled component, it misses characters when typing quickly
         ;; If we leave it uncontrolled, you can't set the input via state directly
         ;; HOWEVER, this will allow you to change state and synchronize that change as long as the input isn't focused
         (when-let [input-el (or (some-> (re/dom-node this) (.querySelector "input"))
                                 (some-> (re/dom-node this) (.querySelector "textarea")))]
           (when (and (not @focused?*) val- (not= (.-value input-el) @val-))
             (set! (.-value input-el) @val-)))
         (when-let [extra-component-did-update (:extra-component-did-update (re/props this))]
           (extra-component-did-update this)))
       :reagent-render
       (fn [{:keys [cmp container-style label required? input-type form-path form-name on-change transf style regex
                    default-value value label-info on-focus focused?* on-blur size extra-on-change clearable?
                    on-clear-click no-border-radius? disabled? form-change-debounce-time label-style json-validation?
                    pre-phosphor-icon pre-icon-style allowed-keys label-class-name input-mode input-style
                    clearable-icon-style pre-contents label-offset post-contents pre-contents-container-style
                    post-contents-container-style container-class-name]
             :or {input-type :text
                  cmp semantic-ui/Input}
             :as args}]
         (let [class-name (some->> [(:class-name args)
                                    (when clearable? "clearable")
                                    (when pre-phosphor-icon "pre-icon")
                                    (when (not-empty pre-contents) "pre-contents")
                                    (when (not-empty post-contents) "post-contents")
                                    (when no-border-radius? "no-border-radius")
                                    (when @theme- (name @theme-))]
                                   (filter identity)
                                   (s/join " "))
               invalid-json-msg (when (and json-validation? (or (when val- (not (s/blank? @val-)))
                                                                (not (s/blank? value))))
                                  (cond (not (util/valid-json? (or (when val- @val-) value)))
                                        "Malformed JSON"
                                        (and allowed-keys (not (util/valid-json-keys? @val- allowed-keys)))
                                        "Invalid JSON keys"))
               label-offset (or label-offset (if (= size :large) 9 8))
               show-clearable-icon? (and clearable? form-path form-name val- (not (s/blank? @val-)))]
           [:div {:class-name container-class-name
                  :style (merge {:display :flex
                                 :flex-direction :column}
                                (when (or clearable? pre-phosphor-icon (not-empty pre-contents)
                                          (not-empty post-contents))
                                  {:position :relative})
                                container-style)}
            (when label
              [lbl/label {:class-name label-class-name
                          :required? required?
                          :info-tooltip label-info
                          :size size
                          :style label-style}
               label])
            (when pre-phosphor-icon
              [icon/phosphor-icon {:icon-type pre-phosphor-icon
                                   :style (merge {:color (or (:color style) (style/color :gray :shade-16))
                                                  :cursor :pointer
                                                  :font-size 24
                                                  :position :absolute
                                                  :top (if label
                                                         (str "calc(50% + " label-offset "px)")
                                                         "50%")
                                                  :left 12
                                                  :transform "translateY(-50%)"
                                                  :z-index (style/z :low)}
                                                 pre-icon-style)}])
            (when pre-contents
              (into [:div {:class-name "pre-content-container"
                           :style (merge {:position :absolute
                                          :top (if label
                                                 (str "calc(50% + " label-offset "px)")
                                                 "50%")
                                          :left 12
                                          :transform "translateY(-50%)"
                                          :z-index (style/z :low)}
                                         pre-contents-container-style)}]
                    pre-contents))
            [:> cmp (cond-> args
                            (and (or default-value
                                     (and val- @val-))
                                 (not value))
                            (assoc :default-value (or default-value (and val- @val-)))

                            value
                            (assoc :value value)

                            size
                            (assoc :size (name size))

                            class-name
                            (assoc :class-name class-name)

                            disabled?
                            (assoc :disabled true)

                            input-style
                            (assoc-in [:input :style] input-style)
                            input-mode
                            (assoc-in [:input :inputMode] input-mode)

                            true
                            (assoc :type input-type
                                   :style style
                                   :on-change (fn [e d]
                                                (let [target (.-target e)
                                                      in-v (aget d "value")
                                                      v (if transf (transf in-v) in-v)]
                                                  (when (and val-
                                                             (not (s/blank? v))
                                                             regex
                                                             (not (re-matches regex v)))
                                                    (set! (.-value target) @val-))
                                                  (when (or (not val-)
                                                            (not regex)
                                                            (s/blank? v)
                                                            (re-matches regex v))
                                                    (when (and target transf)
                                                      (set! (.-value target) v))
                                                    (if (and form-path form-name)
                                                      (if form-change-debounce-time
                                                        (debounced-form-change v)
                                                        (re/dispatch [:form/change form-path form-name v]))
                                                      (when on-change (on-change v))))
                                                  (when extra-on-change
                                                    (extra-on-change v))))
                                   :on-focus #(do
                                                (re/dispatch [:keybind/pause-current-route-bindings])
                                                (when focused?* (reset! focused?* true))
                                                (when on-focus (on-focus %)))
                                   :on-blur #(do
                                               (re/dispatch [:keybind/resume-current-route-bindings])
                                               (when focused?* (reset! focused?* false))
                                               (when on-blur (on-blur %))))

                            true
                            (dissoc :container-style :label :input-type :required? :form-path :form-name
                                    :val-subscribe :cmp :transf :allowed-pattern :regex :label-info
                                    :on-change-override :extra-on-change :focused?* :clearable? :no-border-radius?
                                    :focused? :form-change-debounce-time :extra-component-did-update
                                    :disabled? :label-style :json-validation? :allowed-keys :input-style :input-mode
                                    :label-class-name :x-auto-complete :pre-phosphor-icon :pre-icon-style :pre-contents
                                    :clearable-icon-style :label-offset :post-contents :pre-contents-container-style
                                    :post-contents-container-style :container-class-name))]

            (when invalid-json-msg
              [:div {:style {:color (style/color :red :base)
                             :font-size 13}} invalid-json-msg])

            (when (and post-contents (not show-clearable-icon?))
              (into [:div {:class-name "post-contents-container"
                           :style (merge {:position :absolute
                                          :top (if label
                                                 (str "calc(50% + " label-offset "px)")
                                                 "50%")
                                          :right 5
                                          :transform "translateY(-50%)"
                                          :z-index (style/z :low)}
                                         post-contents-container-style)}]
                    post-contents))

            (when show-clearable-icon?
              [icon/material-icon {:class-name "clearable-input-material-close-icon"
                                   :icon-type :clear
                                   :style (merge {:color (or (:color style) (style/color :gray :base))
                                                  :cursor :pointer
                                                  :position :absolute
                                                  :top (if label
                                                         (str "calc(50% + " label-offset "px)")
                                                         "50%")
                                                  :right 5
                                                  :transform "translateY(-50%)"
                                                  :z-index (style/z :low)}
                                                 clearable-icon-style)
                                   :on-click (or on-clear-click
                                                 #(let [input-el (some-> (.-target %)
                                                                         (.-parentElement)
                                                                         (.querySelector "input"))]
                                                    (set! (.-value input-el) "")
                                                    (re/dispatch [:form/change form-path form-name ""])
                                                    (when extra-on-change (extra-on-change ""))
                                                    (.focus input-el)
                                                    (.preventDefault %)
                                                    (.stopPropagation %)))}])]))})))

(defn primary-input [args]
  [input (assoc args :class-name "primary-input"
                     :label-style {:color (style/color :on-background :base)})])

(defn round-floating-input [args]
  [input (assoc args :class-name "round-floating-input")])

(defn textarea [{:keys [class-name] :as args}]
  [input (assoc args :class-name (->> ["ui" "textarea" class-name]
                                      (filter identity)
                                      (s/join " "))
                     :clearable? false
                     :cmp semantic-ui/TextArea)])

(defn json-textarea [args]
  [input (assoc args :class-name "ui textarea"
                     :clearable? false
                     :json-validation? true
                     :cmp semantic-ui/TextArea)])

(defn json-textarea-with-schema
  [args]
  (let [val- (when-let [{:keys [form-path form-name]} args]
               (re/subscribe [:form/val- form-path form-name]))
        copied-feedback?* (re/ratom false)]
    (fn [{:keys [schema schema-error-message show-actions?
                 form-path form-name on-change]
          :or {show-actions? true}
          :as args}]
      (let [current-value (or @val- "")
            line-count (if (s/blank? current-value)
                         0
                         (count (s/split-lines current-value)))
            char-count (count current-value)
            schema-error (when (and schema (not (s/blank? current-value)))
                           (util/valid-json-with-schema? current-value schema))
            detailed-error (when (and schema (not (s/blank? current-value)) schema-error)
                             (util/explain-json-schema-error current-value schema))
            copy-to-clipboard (fn [text]
                                (when-let [clipboard (.-clipboard js/navigator)]
                                  (.writeText clipboard text)
                                  (reset! copied-feedback?* true)
                                  (js/setTimeout #(reset! copied-feedback?* false) 2000)))
            dispatch-change (fn [new-value]
                              (when form-path
                                (re/dispatch [:form/change form-path form-name new-value]))
                              (when on-change
                                (on-change new-value)))
            format-current (fn []
                             (dispatch-change (util/format-json current-value)))
            clear-value (fn []
                          (when (js/confirm "Clear JSON content?")
                            (dispatch-change "")))
            enhanced-args (-> args
                              (assoc :class-name "ui textarea"
                                     :clearable? false
                                     :json-validation? (get args :json-validation? true)
                                     :cmp semantic-ui/TextArea)
                              (dissoc :schema :schema-error-message
                                      :show-actions? :show-schema?))]
        [:div {:style {:display "flex" :flex-direction "column" :gap "10px"}}
         (when-not (s/blank? current-value)
           [:div {:style {:display "flex" :justify-content "flex-end" :align-items "center"
                          :font-size "12px" :font-weight "600"}}
            [:div {:style {:display "flex" :align-items "center" :gap "5px"
                           :color (if schema-error
                                    (style/color :orange :base)
                                    (style/color :green :base))}}
             (if schema-error
               [icon/material-icon {:icon-type :cancel}]
               [icon/material-icon {:icon-type :check_circle}])
             (if schema-error "Invalid" "Valid")]])
         [input enhanced-args]
         [:div {:style {:display "flex" :gap "12px" :font-size "11px"
                        :color (style/color :gray :base)}}
          [:span (str line-count (if (= 1 line-count) " line" " lines"))]
          [:span (str char-count (if (= 1 char-count) " character" " characters"))]]
         (when show-actions?
           [:div {:style {:display "flex" :gap "6px" :flex-wrap "wrap"}}
            [:button {:class "ui tiny button"
                      :disabled (s/blank? current-value)
                      :on-click format-current}
             [icon/material-icon {:icon-type :format_align_left}]
             "Format"]
            [:button {:class "ui tiny button"
                      :disabled (s/blank? current-value)
                      :on-click #(copy-to-clipboard current-value)}
             [icon/material-icon {:icon-type (if @copied-feedback?* :check :content_copy)}]
             (if @copied-feedback?* "Copied!" "Copy")]
            [:button {:class "ui tiny button"
                      :disabled (s/blank? current-value)
                      :on-click clear-value}
             [icon/material-icon {:icon-type :delete}]
             "Clear"]])
         (when schema-error
           [:div {:style {:background-color (style/color :red :lightest)
                          :border-left (str "4px solid " (style/color :red :base))
                          :padding "12px"
                          :border-radius "4px"}}
            [:div {:style {:color (style/color :red :dark)
                           :font-weight "600"
                           :margin-bottom "6px"}}
             [icon/material-icon {:icon-type :warning}]
             " " (or schema-error-message (:message detailed-error) schema-error)]
            (when-let [human-errors (:human-errors detailed-error)]
              [:div {:style {:margin-top "10px"}}
               [:ul {:style {:margin "0" :padding-left "20px" :line-height "1.6"}}
                (for [{:keys [field message]} human-errors]
                  [:li {:key field
                        :style {:color (style/color :red :dark)
                                :font-size "13px"}}
                   [:strong field] ": " message])]])
            (when (:errors detailed-error)
              [:div {:style {:font-size "12px"
                             :color (style/color :red :base)
                             :margin-top "12px"
                             :font-family "monospace"
                             :background-color "white"
                             :padding "8px"
                             :border-radius "3px"
                             :overflow-x "auto"}}
               [:details
                [:summary {:style {:cursor "pointer" :font-weight "600"}}
                 "Technical Details"]
                [:pre {:style {:margin "8px 0 0 0"
                               :overflow-x "auto"
                               :white-space "pre-wrap"
                               :word-break "break-word"
                               :max-width "100%"}}
                 (:errors detailed-error)]]])])]))))
